import { Button } from 'components/ui/button';
import React, { useEffect, useState } from 'react';
import {
    closestCenter,
    DndContext,
    MouseSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button as MuiButton, CircularProgress, Box } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ScreenGroup } from 'types';
import { createScreenGroup, deleteScreenGroup, fetchScreenGroups, updateScreenGroup } from 'core/API/ScreenSetting';
import { Snackbar, Alert } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import styles from './ScreenSetting.module.scss';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import CreateScreenGroupSettings from './CreateScreenGroupSettings';
import EditScreenGroupSettings from './EditScreenGroupSettings';

const SortableItem = ({ id, children, disabled }: { id: string | number; children: React.ReactNode; disabled?: boolean }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <li ref={setNodeRef} style={style} className={styles.sortableItem}>
            <div
                {...(disabled ? {} : attributes)}
                {...(disabled ? {} : listeners)}
                className={styles.gripHandle}
                style={{
                    cursor: disabled ? 'not-allowed' : 'grab',
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <GripVertical className={styles.gripIcon} />
            </div>
            <div className={styles.itemContent}>{children}</div>
        </li>
    );
};

interface ScreenSettingProps {
    onViewModeChange?: (mode: 'list' | 'create' | 'editItem' | 'edit') => void;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
}

const ScreenSetting: React.FC<ScreenSettingProps> = ({
    onViewModeChange,
    canEdit = false,
    canDelete = false,
    canExecute = false,
}) => {
    const { workplaceId } = useWorkplacesContext();
    const [isExpanded, setIsExpanded] = useState(true);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<ScreenGroup | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'create' | 'editItem' | 'edit'>('list');
    const [selectedScreenGroup, setSelectedScreenGroup] = useState<ScreenGroup | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
    const [editingLabel, setEditingLabel] = useState<string>('');
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newGroupLabel, setNewGroupLabel] = useState<string>('');
    const [isSavingInline, setIsSavingInline] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const queryClient = useQueryClient();

    const { data: screenGroups = [], isLoading } = useQuery({
        queryKey: ['screenGroups', workplaceId],
        queryFn: () => fetchScreenGroups(workplaceId),
        enabled: !!workplaceId,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const [tvGroups, setTvGroups] = useState<ScreenGroup[]>(screenGroups);


    const deleteMutation = useMutation({
        mutationFn: deleteScreenGroup,
        onSuccess: async () => {
            // Only invalidate - useQuery will handle refetch automatically
            await queryClient.invalidateQueries(['screenGroups', workplaceId]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceId]);
            setSnackbar({
                open: true,
                message: 'Screen group deleted successfully',
                severity: 'success',
            });
        },
        onError: (error) => {
            console.error('Error deleting screen group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete screen group. Please try again.',
                severity: 'error',
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async (sortedTvGroups: ScreenGroup[]) => {
            await Promise.all(
                sortedTvGroups.map((group) =>
                    updateScreenGroup({
                        screenGroupID: group.id.toString(),
                        data: {
                            label: group.label,
                            workplace: group.workplace,
                            list_order: group.list_order,
                            deleted: group.deleted,
                        },
                    })
                )
            );
        },
        onSuccess: async () => {
            // Only invalidate - useQuery will handle refetch automatically
            await queryClient.invalidateQueries(['screenGroups', workplaceId]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceId]);
        },
        onError: (error) => {
            console.error('Error updating screen groups:', error);
        },
    });


    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    useEffect(() => {
        if (!isLoading) {
            setTvGroups(screenGroups);
        }
    }, [screenGroups, isLoading]);

    // Reset add/edit states when viewMode changes or component mounts
    useEffect(() => {
        if (viewMode !== 'edit') {
            setIsAddingNew(false);
            setNewGroupLabel('');
            setEditingGroupId(null);
            setEditingLabel('');
        }
    }, [viewMode]);

    // Safety mechanism: Close dialog if it's been open too long (prevent stuck state)
    useEffect(() => {
        if (isDeleteDialogOpen) {
            const timeout = setTimeout(() => {
                console.warn('Delete dialog was open for too long, forcing close');
                setIsDeleteDialogOpen(false);
                setGroupToDelete(null);
            }, 300000); // 5 minutes timeout

            return () => clearTimeout(timeout);
        }
    }, [isDeleteDialogOpen]);

    const handleTvGroupDragEnd = (event: any) => {
        if (!canEdit) {
            return;
        }
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = tvGroups.findIndex((group) => group.id === active.id);
            const newIndex = tvGroups.findIndex((group) => group.id === over.id);
            const reorderedGroups = arrayMove(tvGroups, oldIndex, newIndex).map((group, index) => ({
                ...group,
                list_order: index + 1,
            }));

            setTvGroups(reorderedGroups);
            queryClient.setQueryData(['screenGroups', workplaceId], reorderedGroups);
            // Auto-save on drag end
            updateMutation.mutate(reorderedGroups);
        }
    };

    const handleAddClick = () => {
        if (!canExecute) {
            return;
        }
        setIsAddingNew(true);
        setNewGroupLabel('');
        setEditingGroupId(null);
    };

    const handleEditClick = (group: ScreenGroup) => {
        if (!canEdit) {
            return;
        }
        setEditingGroupId(group.id);
        setEditingLabel(group.label);
        setIsAddingNew(false);
    };

    const handleCancelInlineEdit = () => {
        setEditingGroupId(null);
        setEditingLabel('');
    };

    const handleCancelInlineAdd = () => {
        setIsAddingNew(false);
        setNewGroupLabel('');
    };

    const handleSaveInlineEdit = async (group: ScreenGroup) => {
        if (!canEdit) {
            return;
        }
        if (!editingLabel.trim()) {
            setSnackbar({
                open: true,
                message: 'Please enter a screen group label.',
                severity: 'error',
            });
            return;
        }

        // Check for duplicate (excluding current group)
        const isDuplicate = tvGroups.some(
            (g) => g.id !== group.id && g.label.toLowerCase() === editingLabel.trim().toLowerCase()
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: 'This screen group label already exists.',
                severity: 'error',
            });
            return;
        }

        setIsSavingInline(true);
        try {
            await updateScreenGroup({
                screenGroupID: group.id.toString(),
                data: {
                    label: editingLabel.trim(),
                    workplace: group.workplace,
                    list_order: group.list_order,
                    deleted: group.deleted,
                },
            });
            await queryClient.invalidateQueries(['screenGroups', workplaceId]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceId]);
            setSnackbar({
                open: true,
                message: 'Screen group updated successfully',
                severity: 'success',
            });
            setEditingGroupId(null);
            setEditingLabel('');
        } catch (error: any) {
            console.error('Error updating screen group:', error);
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || 'Failed to update screen group. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsSavingInline(false);
        }
    };

    const handleSaveInlineAdd = async () => {
        if (!canExecute) {
            return;
        }
        if (!newGroupLabel.trim()) {
            setSnackbar({
                open: true,
                message: 'Please enter a screen group label.',
                severity: 'error',
            });
            return;
        }

        // Check for duplicate
        const isDuplicate = tvGroups.some(
            (g) => g.label.toLowerCase() === newGroupLabel.trim().toLowerCase()
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: 'This screen group label already exists.',
                severity: 'error',
            });
            return;
        }

        setIsSavingInline(true);
        try {
            const workplaceIdValue = workplaceId || Number(localStorage.getItem("workplace-id"));
            // Add at the end - priority should be last
            const newOrder = tvGroups.length + 1;
            await createScreenGroup({
                label: newGroupLabel.trim(),
                workplace: { id: workplaceIdValue },
                list_order: newOrder,
                deleted: false,
            });
            
            await queryClient.invalidateQueries(['screenGroups', workplaceId]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceId]);
            setSnackbar({
                open: true,
                message: 'Screen group created successfully',
                severity: 'success',
            });
            // Clear add state after successful save
            setIsAddingNew(false);
            setNewGroupLabel('');
        } catch (error: any) {
            console.error('Error creating screen group:', error);
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || 'Failed to create screen group. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsSavingInline(false);
        }
    };

    const handleBackClick = () => {
        setViewMode('list');
        setSelectedScreenGroup(null);
        onViewModeChange?.('list');
    };

    const handleEditModeClick = () => {
        if (!canEdit) {
            return;
        }
        setViewMode('edit');
        onViewModeChange?.('edit');
    };

    const handleEditModeBackClick = () => {
        setViewMode('list');
        onViewModeChange?.('list');
    };

    // Notify parent of initial viewMode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleScreenGroupCreated = () => {
        setSnackbar({
            open: true,
            message: 'Screen group created successfully',
            severity: 'success',
        });
        setViewMode('list');
        onViewModeChange?.('list');
    };

    const handleScreenGroupUpdated = () => {
        setSnackbar({
            open: true,
            message: 'Screen group updated successfully',
            severity: 'success',
        });
        setViewMode('list');
        onViewModeChange?.('list');
    };

    const handleDeleteHandler = (group: ScreenGroup) => {
        if (!canDelete) {
            return;
        }
        setGroupToDelete(group);
        setIsDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const handleConfirmedDelete = async () => {
        if (!groupToDelete) return;
        
        try {
            await deleteMutation.mutateAsync(groupToDelete.id.toString());
        } catch (error) {
            // Error is handled in mutation onError
        } finally {
            setIsDeleteDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const toggleAccordion = () => {
        setIsExpanded(!isExpanded);
    };

    const renderDialogs = () => (
        <>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the screen group{' '}
                        {groupToDelete && <strong>&ldquo;{groupToDelete.label}&rdquo;</strong>}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleCancelDelete} style={{ color: 'black' }}>
                        Cancel
                    </MuiButton>
                    <MuiButton
                        style={{ backgroundColor: 'red', color: 'white' }}
                        onClick={handleConfirmedDelete}
                        disabled={deleteMutation.isLoading}
                    >
                        {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                    </MuiButton>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );

    switch (viewMode) {
        case 'create':
            return (
                <>
                    <CreateScreenGroupSettings
                        screenGroups={tvGroups}
                        handleBackClick={handleBackClick}
                        onScreenGroupCreated={handleScreenGroupCreated}
                    />
                    {renderDialogs()}
                </>
            );

        case 'editItem':
            return selectedScreenGroup ? (
                <>
                    <EditScreenGroupSettings
                        screenGroup={selectedScreenGroup}
                        screenGroups={tvGroups}
                        handleBackClick={handleBackClick}
                        onScreenGroupUpdated={handleScreenGroupUpdated}
                    />
                    {renderDialogs()}
                </>
            ) : null;

        case 'edit':
            return (
                <>
                    <div style={{ width: '100%', margin: '10px 0', overflow: 'hidden', fontFamily: 'var(--Gotham)' }}>
                        <div className={styles.accordionHeader}>
                            <button onClick={toggleAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <h5>Screen Groups</h5>
                            </button>
                        </div>
                        <div
                            className={`${styles.accordionContent} ${isExpanded ? styles.expanded : styles.collapsed}`}
                        >
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', padding: '40px' }}>
                                    <CircularProgress size={25} />
                                </Box>
                            ) : (
                                <div className={styles.content}>
                                    <div className={styles.deviceCard}>
                                        <div className={styles.editModeButtons}>
                                            <button
                                                className={styles.backButton}
                                                onClick={handleEditModeBackClick}
                                            >
                                                Back
                                            </button>
                                            <button 
                                                className={styles.addButton} 
                                                onClick={handleAddClick}
                                                disabled={isAddingNew || editingGroupId !== null || !canExecute}
                                                title={canExecute ? "Add new screen group" : "You do not have permission to add"}
                                                style={{
                                                    opacity: !canExecute ? 0.5 : 1,
                                                    cursor: !canExecute ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {/* Inline Add Row above table box */}
                                        {isAddingNew && (
                                            <div style={{ 
                                                marginBottom: '16px', 
                                                padding: '12px 16px', 
                                                backgroundColor: '#fff', 
                                                border: '1px solid #ccc', 
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}>
                                                <input
                                                    type="text"
                                                    value={newGroupLabel}
                                                    onChange={(e) => setNewGroupLabel(e.target.value)}
                                                    placeholder="Enter screen group name"
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px 12px',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                    }}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleSaveInlineAdd();
                                                        } else if (e.key === 'Escape') {
                                                            handleCancelInlineAdd();
                                                        }
                                                    }}
                                                />
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <button
                                                        onClick={handleSaveInlineAdd}
                                                        disabled={isSavingInline || !newGroupLabel.trim() || !canExecute}
                                                        title={canExecute ? "Save" : "You do not have permission to add"}
                                                        style={{
                                                            backgroundColor: 'rgb(68, 99, 136)',
                                                            color: 'white',
                                                            padding: '6px 12px',
                                                            borderRadius: '4px',
                                                            border: 'none',
                                                            cursor: isSavingInline || !newGroupLabel.trim() || !canExecute ? 'not-allowed' : 'pointer',
                                                            opacity: isSavingInline || !newGroupLabel.trim() || !canExecute ? 0.6 : 1,
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        {isSavingInline ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                        onClick={handleCancelInlineAdd}
                                                        disabled={isSavingInline}
                                                        title="Cancel"
                                                        style={{
                                                            backgroundColor: '#fff',
                                                            color: '#333',
                                                            padding: '6px 12px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ccc',
                                                            cursor: isSavingInline ? 'not-allowed' : 'pointer',
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className={styles.sortSection}>
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragEnd={handleTvGroupDragEnd}
                                            >
                                                <SortableContext
                                                    items={tvGroups.map((group) => group.id)}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    <ul className={styles.sortableList}>
                                                        {tvGroups.length > 0 ? (
                                                            tvGroups.map((group, index) => (
                                                                <SortableItem key={group.id} id={group.id} disabled={!canEdit}>
                                                                    <div className={styles.item}>
                                                                        {editingGroupId === group.id ? (
                                                                            // Inline Edit Mode
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                                                                <span style={{ minWidth: '60px', fontWeight: '500' }}>
                                                                                    {index + 1}:
                                                                                </span>
                                                                                <input
                                                                                    type="text"
                                                                                    value={editingLabel}
                                                                                    onChange={(e) => setEditingLabel(e.target.value)}
                                                                                    style={{
                                                                                        flex: 1,
                                                                                        padding: '8px 12px',
                                                                                        border: '1px solid #ccc',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '14px',
                                                                                    }}
                                                                                    autoFocus
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter') {
                                                                                            handleSaveInlineEdit(group);
                                                                                        } else if (e.key === 'Escape') {
                                                                                            handleCancelInlineEdit();
                                                                                        }
                                                                                    }}
                                                                                />
                                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                                    <button
                                                                                        className={styles.iconButton}
                                                                                        onClick={() => handleSaveInlineEdit(group)}
                                                                                        disabled={isSavingInline || !editingLabel.trim() || !canEdit}
                                                                                        title={canEdit ? "Save" : "You do not have permission to edit"}
                                                                                        style={{
                                                                                            backgroundColor: 'rgb(68, 99, 136)',
                                                                                            color: 'white',
                                                                                            padding: '6px 12px',
                                                                                            borderRadius: '4px',
                                                                                            border: 'none',
                                                                                            cursor: isSavingInline || !editingLabel.trim() || !canEdit ? 'not-allowed' : 'pointer',
                                                                                            opacity: isSavingInline || !editingLabel.trim() || !canEdit ? 0.6 : 1,
                                                                                        }}
                                                                                    >
                                                                                        {isSavingInline ? 'Saving...' : 'Save'}
                                                                                    </button>
                                                                                    <button
                                                                                        className={styles.iconButton}
                                                                                        onClick={handleCancelInlineEdit}
                                                                                        disabled={isSavingInline}
                                                                                        title="Cancel"
                                                                                        style={{
                                                                                            backgroundColor: '#fff',
                                                                                            color: '#333',
                                                                                            padding: '6px 12px',
                                                                                            borderRadius: '4px',
                                                                                            border: '1px solid #ccc',
                                                                                            cursor: isSavingInline ? 'not-allowed' : 'pointer',
                                                                                        }}
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            // Normal View Mode
                                                                            <>
                                                                                <span>
                                                                                    {index + 1}: {group.label}
                                                                                </span>
                                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                                    <button
                                                                                        className={styles.iconButton}
                                                                                        onClick={() => handleEditClick(group)}
                                                                                        disabled={isAddingNew || editingGroupId !== null || !canEdit}
                                                                                        title={canEdit ? "Edit Screen Group" : "You do not have permission to edit"}
                                                                                        style={{
                                                                                            opacity: !canEdit ? 0.5 : 1,
                                                                                            cursor: !canEdit ? 'not-allowed' : 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        <Edit className={styles.editIcon} />
                                                                                    </button>
                                                                                    <button
                                                                                        className={styles.iconButton}
                                                                                        onClick={() => handleDeleteHandler(group)}
                                                                                        disabled={isAddingNew || editingGroupId !== null || !canDelete}
                                                                                        title={canDelete ? "Delete Screen Group" : "You do not have permission to delete"}
                                                                                        style={{
                                                                                            opacity: !canDelete ? 0.5 : 1,
                                                                                            cursor: !canDelete ? 'not-allowed' : 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        <Delete className={styles.deleteIcon} />
                                                                                    </button>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </SortableItem>
                                                            ))
                                                        ) : !isAddingNew ? (
                                                            <div className={styles.noDataMessage}>No screen groups found.</div>
                                                        ) : null}
                                                    </ul>
                                                </SortableContext>
                                            </DndContext>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {renderDialogs()}
                </>
            );

        case 'list':
        default:
            return (
                <>
                    <div style={{ width: '100%', margin: '10px 0', overflow: 'hidden', fontFamily: 'var(--Gotham)' }}>
                        <div className={styles.accordionHeader}>
                            <button onClick={toggleAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <h5>Screen Groups</h5>
                            </button>
                            <button
                                className={styles.editButton}
                                onClick={handleEditModeClick}
                                title={canEdit ? "Edit Screen Groups" : "You do not have permission to edit"}
                                style={{ marginLeft: 'auto', opacity: canEdit ? 1 : 0.5, cursor: canEdit ? 'pointer' : 'not-allowed' }}
                                disabled={!canEdit}
                            >
                                <Edit className={styles.editIcon} />
                            </button>
                        </div>
                        <div
                            className={`${styles.accordionContent} ${isExpanded ? styles.expanded : styles.collapsed}`}
                        >
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', padding: '40px' }}>
                                    <CircularProgress size={25} />
                                </Box>
                            ) : (
                                <div className={styles.content}>
                                    <div className={styles.deviceCard}>

                                        {tvGroups.length > 0 ? (
                                            <div className={styles.tableContainer}>
                                                <table className={styles.screenGroupTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Order</th>
                                                            <th>Screen Group Name</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tvGroups.map((group, index) => (
                                                            <tr key={group.id}>
                                                                <td>{index + 1}</td>
                                                                <td>{group.label}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className={styles.noDataMessage}>No screen groups found.</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {renderDialogs()}
                </>
            );
    }
};

export default ScreenSetting;